import { useCreateTherapyAttribute } from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreateTherapyAttributeRequest } from '@repo/types/src/glow-up';
import { CirclePlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { label: 'Actif', value: 'active' },
  { label: 'Inactif', value: 'inactive' },
];

export const AddTherapyAttribute = ({
  refetch,
}: {
  refetch: () => void;
}) => {
  const mutation = useCreateTherapyAttribute();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreateTherapyAttributeRequest>({
    name: '',
    status: 'active',
  });

  const handleSubmit = () => {
    if (!formData.name?.trim()) {
      toast.error('Veuillez renseigner un nom');
      return;
    }

    mutation.mutate(
      {
        name: formData.name.trim(),
        status: formData.status ?? 'active',
      },
      {
        onSuccess: (data) => {
          setOpen(false);
          setFormData({ name: '', status: 'active' });
          refetch();
          toast.success(
            `L’attribut de thérapie "${data.name}" a été créé avec succès !`,
            { duration: 3000 },
          );
        },
        onError: () => {
          toast.error(
            `Erreur lors de la création de l’attribut "${formData.name}"`,
            { duration: 3000 },
          );
        },
      },
    );
  };

  const isLoading = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <div className="flex flex-row gap-2 items-center">
            <CirclePlus size={24} />
            <span>Ajouter un attribut</span>
          </div>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-center">
            Ajouter un attribut de thérapie
          </DialogTitle>
          <DialogDescription className="text-center">
            Définissez un nouvel attribut utilisé dans les objectifs
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <Input
            type="text"
            placeholder="Nom de l’attribut"
            value={formData.name}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, name: event.target.value }))
            }
            disabled={isLoading}
          />

          <Select
            value={formData.status ?? 'active'}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, status: value }))
            }
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={isLoading}>
              Annuler
            </Button>
          </DialogClose>
          <Button variant="default" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Création...' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
